/** (구)마케팅 건별 진행(83) — 레거시 appr_edit_forms_mkt_by_case */
export const MARKETING_CASE_TYPE = 83;

export const isMarketingCaseType = (apvType: number): boolean =>
  apvType === MARKETING_CASE_TYPE;

export const MARKETING_CASE_EMPTY_MESSAGE = '진행할 내역이 없습니다.';
export const MARKETING_CASE_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** 비용 건 키 — 작성·편집 요청의 marketingCase 와 같은 모양 */
export type MarketingCaseKey = {
  brandCode: string;
  season: string;
  costDocId: string;
  approvalDate: string;
};

export type MarketingCaseCandidate = MarketingCaseKey & {
  brandName: string | null;
  amount: string | null;
  documentYearMonth: string | null;
  title: string | null;
  termStart: string | null;
  termEnd: string | null;
  writerName: string | null;
};

export type MarketingCaseBrand = {
  brandCode: string;
  brandName: string | null;
  isParticipating: boolean;
};

export type MarketingCaseHeader = {
  brandCode?: string | null;
  brandName?: string | null;
  season?: string | null;
  costDocId?: string | null;
  title?: string | null;
  purpose?: string | null;
  termStart?: string | null;
  termEnd?: string | null;
  imagePath?: string | null;
  imageUrl?: string | null;
  brands?: MarketingCaseBrand[] | null;
};

export type MarketingCaseProgressDetail = {
  sequence?: string | null;
  title?: string | null;
  detail?: string | null;
};

export type MarketingCaseCost = {
  offerAmount?: string | null;
  negotiatedAmount?: string | null;
  negotiationRate?: string | null;
};

export type MarketingCaseEstimateItem = {
  sequence?: string | null;
  categoryName?: string | null;
  subCategoryName?: string | null;
  description?: string | null;
  price?: string | null;
  quantity?: string | null;
  amount?: string | null;
  paymentTypeName?: string | null;
  vendorCode?: string | null;
  vendorName?: string | null;
};

export type MarketingCaseEstimateGroup = {
  brandCode?: string | null;
  brandName?: string | null;
  items?: MarketingCaseEstimateItem[] | null;
  subtotalPrice?: string | null;
  subtotalAmount?: string | null;
};

export type MarketingCaseDisplay = {
  candidates: MarketingCaseCandidate[] | null;
  selection: MarketingCaseKey | null;
  subject: string | null;
  header: MarketingCaseHeader | null;
  progressDetails: MarketingCaseProgressDetail[] | null;
  cost: MarketingCaseCost | null;
  estimate: MarketingCaseEstimateGroup[] | null;
};

export type MarketingCaseDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: MarketingCaseDisplay;
};

/** 「확인」으로 확정한 건 — 저장 attributes 와 같은 모양 */
export type MarketingCaseState = MarketingCaseKey & {
  /** 작성 시점 표 HTML(견적 상세 모달 내용 포함) */
  content: string | null;
  /** 이 화면에서 새로 고른 건 — 저장 때 marketingCase 로 보내 비용 건에 연결 */
  linkPending: boolean;
};

const text = (v: unknown): string | null => {
  if (typeof v === 'number') return String(v);
  return typeof v === 'string' && v.trim() ? v : null;
};

export const parseMarketingCaseAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): MarketingCaseState | null => {
  const brandCode = text(attributes?.brandCode);
  const season = text(attributes?.season);
  const costDocId = text(attributes?.costDocId);
  const approvalDate = text(attributes?.approvalDate);
  if (!attributes || !brandCode || !season || !costDocId || !approvalDate)
    return null;
  return {
    brandCode,
    season,
    costDocId,
    approvalDate,
    content: text(attributes.content),
    linkPending: false,
  };
};

export const buildMarketingCaseAttributes = (
  state: MarketingCaseState | null | undefined,
) =>
  state
    ? {
        brandCode: state.brandCode,
        season: state.season,
        costDocId: state.costDocId,
        approvalDate: state.approvalDate,
        content: state.content,
      }
    : null;

/**
 * 작성·편집 요청 최상위 marketingCase. 편집으로 연 문서는 보내지 않음 — 생략하면 BE 가 기존 연결을 두고
 * 상태(S/P)만 바꾸고, 보내면 비용 건 스냅샷을 지금 값으로 다시 씀
 */
export const buildMarketingCaseLink = (
  state: MarketingCaseState | null | undefined,
): MarketingCaseKey | null =>
  state?.linkPending
    ? {
        brandCode: state.brandCode,
        season: state.season,
        costDocId: state.costDocId,
        approvalDate: state.approvalDate,
      }
    : null;

/** 레거시는 임시저장·상신 모두 검사 */
export const checkMarketingCaseBeforeSave = (
  state: MarketingCaseState | null | undefined,
): string | null => (state?.content ? null : MARKETING_CASE_MISSING_MESSAGE);

export const marketingCaseKey = (
  state: MarketingCaseState | null | undefined,
) =>
  state
    ? `${state.brandCode}|${state.season}|${state.costDocId}|${state.approvalDate}`
    : '';

export const marketingCaseCandidateKey = (c: MarketingCaseKey) =>
  `${c.brandCode}|${c.season}|${c.costDocId}|${c.approvalDate}`;

/** 레거시 콤보 TITLE — 슬래시 앞에만 공백 */
export const buildMarketingCaseCandidateLabel = (c: MarketingCaseCandidate) =>
  `${c.title ?? ''} /${c.documentYearMonth ?? ''} /${c.writerName ?? ''}`;

/** 레거시 number_format — 빈 값은 0, 숫자가 아니면 원문 그대로 */
export const formatMarketingCaseNumber = (
  value: unknown,
  decimals = 0,
): string => {
  if (value === null || value === undefined || value === '')
    return (0).toFixed(decimals);
  const n = Number(value);
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : String(value);
};

/** https 포탈에서 http 이미지는 막힘 — 이미지 서버(pds)가 https 도 응답 */
export const toMarketingCaseImageUrl = (url: string | null | undefined) =>
  url ? url.replace(/^http:\/\//i, 'https://') : null;

/** 견적 상세 Grand Total — 레거시는 소계 행 총액의 합 */
export const sumMarketingCaseEstimate = (
  groups: MarketingCaseEstimateGroup[] | null | undefined,
) =>
  (groups ?? []).reduce((acc, g) => acc + (Number(g.subtotalAmount) || 0), 0);
